import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createApiClient, apiClient } from '../../../api/apiClient';
import { getSessionLockService, initializeSessionLockService } from '../../../api/sessionLockService';
import { View, StyleSheet, ScrollView, ActivityIndicator, Text, TouchableOpacity } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { setCreateSchedulePeriodWeek } from '../../../store/slices/dloSlice';
import { fetchKCPHData, fetchDashboardHours, fetchDashboardSchedule, copySchedule, cleanScheduleSettings } from '../../../store/thunks/dloThunks';
import { 
  selectKCPHData, 
  selectKCPHLoading, 
  selectKCPHError,
  selectHoursData,
  selectHoursLoading,
  selectHoursError,
  selectScheduleData,
  selectScheduleLoading,
  selectScheduleError
} from '../../../store/slices/dloSlice';
import { DLOLaborOptimizationTitle } from '../components/DLOTitle';
import DLOBarChart, { ChartDataPoint, LegendItem } from '../components/DLOBarChart';
import DLOHorizontalBarChart, { HorizontalChartDataPoint } from '../components/DLOHorizontalBarChart';
import DLOScheduleTablePagination from '../components/Tables/DLOScheduleTablePagination';
import DLOScheduleTableHeader from '../components/Tables/DLOScheduleTableHeader';
import DLOScheduleTableContent from '../components/Tables/DLOScheduleTableContent';
import { ScheduleRowData } from '../components/Tables/DLOScheduleTableContent';
import { RangeFilterValue } from '../components/Tables/DLORangeFilter';
import DLOCreateScheduleModal, { CreateScheduleData } from '../components/DLOCreateScheduleModal';
import DLOLoader from '../components/DLOLoader';
import DLOModal from '../components/DLOModal';
import DLODropdown from '../components/DLODropdown';
import { DLODropdownOption } from '../types/dlo.types';
import { getPeriodOptions, getWeekOptions, getYearOptions } from '../../../utils/filterOptions';
import { toBackendWeek, fromBackendWeek } from '../utils/weekTransforms';
import { formatUserName } from '../../../utils/emailTouserName';

interface ChartFilterValue {
  period: DLODropdownOption | null;
  week: DLODropdownOption | null;
  year: DLODropdownOption | null;
}

interface DLODashboardProps {
  onNavigate?: (screen: string, params?: { [key: string]: any }) => void;
}

export default function DLODashboard({ onNavigate }: DLODashboardProps) {
  // Success message for delete action
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);
  // Error message for delete action
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Timer for auto-dismiss error
  React.useEffect(() => {
    if (deleteError) {
      const timer = setTimeout(() => setDeleteError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [deleteError]);

  // Timer for auto-dismiss success
  React.useEffect(() => {
    if (deleteSuccess) {
      const timer = setTimeout(() => setDeleteSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [deleteSuccess]);
  const dispatch = useDispatch();
  const api = createApiClient();

  // Redux selectors for KCPH data
  const kcphData = useSelector(selectKCPHData);
  const kcphLoading = useSelector(selectKCPHLoading);
  const kcphError = useSelector(selectKCPHError);
  
  // Redux selectors for Dashboard Hours data
  const hoursData = useSelector(selectHoursData);
  const hoursLoading = useSelector(selectHoursLoading);
  const hoursError = useSelector(selectHoursError);
  
  // Dashboard Schedule data selectors
  const scheduleData = useSelector((state: any) => state.dlo.dashboard.scheduleData);
  const filteredScheduleData = useSelector((state: any) => state.dlo.dashboard.filteredScheduleData);
  const scheduleLoading = useSelector((state: any) => state.dlo.dashboard.scheduleLoading);
  const scheduleError = useSelector((state: any) => state.dlo.dashboard.scheduleError);
  const filteredScheduleLoading = useSelector((state: any) => state.dlo.dashboard.filteredScheduleLoading);
  const filteredScheduleError = useSelector((state: any) => state.dlo.dashboard.filteredScheduleError);
  
  // Copy Schedule data selectors
  // const copyScheduleLoading = useSelector(selectCopyScheduleLoading);
  // const copyScheduleError = useSelector(selectCopyScheduleError);
  
  // Redux selector for appData periodWeek
  const periodWeekData = useSelector((state: any) => state.appData.periodWeek?.data);
  
  // Redux selector for current location
  const currentLocation = useSelector((state: any) => state.appData.selectedLocation);
  
  // State for managing table selections
  const [selectedCount, setSelectedCount] = useState(0);
  const [selectedScheduleData, setSelectedScheduleData] = useState<any>(null); // Track selected schedule for copy operations
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set()); // Track all selected row IDs

  // State for table filters
  const [periodFilter, setPeriodFilter] = useState<DLODropdownOption | null>(null);
  const [weekFilter, setWeekFilter] = useState<DLODropdownOption | null>(null);
  const [yearFilter, setYearFilter] = useState<DLODropdownOption | null>(null);
  const [forecastedHoursFilter, setForecastedHoursFilter] = useState<DLODropdownOption | null>(null);
  const [scheduledHoursFilter, setScheduledHoursFilter] = useState<DLODropdownOption | null>(null);
  const [actualHoursFilter, setActualHoursFilter] = useState<DLODropdownOption | null>(null);
  const [actualCasesFilter, setActualCasesFilter] = useState<DLODropdownOption | null>(null);
  const [createdByFilter, setCreatedByFilter] = useState<DLODropdownOption | null>(null);
  const [modifiedByFilter, setModifiedByFilter] = useState<DLODropdownOption | null>(null);

  // State for range filters
  const [headcountRangeFilter, setHeadcountRangeFilter] = useState<{ min?: number; max?: number } | null>(null);
  const [actualKcphRangeFilter, setActualKcphRangeFilter] = useState<{ min?: number; max?: number } | null>(null);
  const [forecastedHoursRangeFilter, setForecastedHoursRangeFilter] = useState<{ min?: number; max?: number } | null>(null);
  const [forecastedCasesRangeFilter, setForecastedCasesRangeFilter] = useState<{ min?: number; max?: number } | null>(null);
  const [actualCasesRangeFilter, setActualCasesRangeFilter] = useState<{ min?: number; max?: number } | null>(null);

  // Helper functions to transform filter options to DLODropdownOption format
  const getDLOPeriodOptions = (): DLODropdownOption[] => {
    return getPeriodOptions().map(option => ({
      id: option.id,
      label: `${option.displayText}`,
      value: option.id,
    }));
  };

  const getDLOWeekOptions = (): DLODropdownOption[] => {
    return getWeekOptions().map(option => ({
      id: option.id,
      label: `${option.displayText}`,
      value: option.id,
    }));
  };

  const getDLOYearOptions = (): DLODropdownOption[] => {
    return getYearOptions().map(option => ({
      id: option.id,
      label: option.displayText,
      value: option.id,
    }));
  };

  // Get current period, week, year from Redux appData
  const currentValues = {
    period: periodWeekData?.period || 9, // Default fallback
    week: periodWeekData?.week || 3,     // Default fallback  
    year: new Date().getFullYear()       // Current year
  };
  
  
  // Initialize chart filters with current values
  const [chartFilters, setChartFilters] = useState<ChartFilterValue>({
    period: {
      id: String(currentValues.period),
      label: `${currentValues.period}`,
      value: String(currentValues.period),
    },
    week: {
      id: String(currentValues.week),
      label: `${currentValues.week}`,
      value: String(currentValues.week),
    },
    year: {
      id: String(currentValues.year),
      label: `${currentValues.year}`,
      value: String(currentValues.year),
    },
  });

  // State for filter modal
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [tempFilters, setTempFilters] = useState<ChartFilterValue>(chartFilters);

  // State for Create Schedule Modal
  const [showCreateScheduleModal, setShowCreateScheduleModal] = useState(false);

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // State for sorting
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Pagination helper functions

  // Helper: sort function for table columns
  const sortTableData = (data: ScheduleRowData[]): ScheduleRowData[] => {
    if (!sortColumn) return data;
    const sorted = [...data].sort((a, b) => {
      let aValue = a[sortColumn as keyof ScheduleRowData];
      let bValue = b[sortColumn as keyof ScheduleRowData];
      // Try to compare as numbers if possible
      const aNum = Number(aValue);
      const bNum = Number(bValue);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }
      // Fallback to string comparison
      aValue = aValue === undefined || aValue === null ? '' : aValue;
      bValue = bValue === undefined || bValue === null ? '' : bValue;
      return sortDirection === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
    return sorted;
  };

  const getAllTableData = (): ScheduleRowData[] => {
    const rawData = transformScheduleToTableData();
    return sortTableData(rawData);
  };

  const getPaginatedData = (): ScheduleRowData[] => {
    const allData = getAllTableData();
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return allData.slice(startIndex, endIndex);
  };

  const getTotalPages = (): number => {
    const allData = getAllTableData();
    return Math.ceil(allData.length / rowsPerPage);
  };

  const getTotalItems = (): number => {
    return getAllTableData().length;
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    console.log(`Page changed to: ${page}`);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    // Reset to first page when changing rows per page
    setCurrentPage(1);
    console.log(`Rows per page changed to: ${newRowsPerPage}`);
  };

  // Fetch KCPH, Dashboard Hours, and Dashboard Schedule data on component mount / location change
  useEffect(() => {
    console.log('DLODashboard: Fetching KCPH, Dashboard Hours, and Dashboard Schedule data...');
    dispatch(fetchKCPHData({}) as any);
    dispatch(fetchDashboardHours({}) as any);
    dispatch(fetchDashboardSchedule({}) as any);
  }, [dispatch, currentLocation]);

  // Function to build filter payload and trigger API call
  const fetchScheduleWithFilters = useCallback(() => {
    const filters: any = {};
    if (yearFilter?.value) filters.year = yearFilter.value;
    if (periodFilter?.value) filters.period = periodFilter.value;
    if (weekFilter?.value && periodFilter?.value) {
      // Always send backend week value
      filters.week = toBackendWeek(Number(periodFilter.value), Number(weekFilter.value));
    }
    if (createdByFilter?.value) filters.createdBy = createdByFilter.value;
    if (modifiedByFilter?.value) filters.modifiedBy = modifiedByFilter.value;
    
    // Add range filters
    if (headcountRangeFilter) {
      filters.headcount = {};
      if (headcountRangeFilter.min !== undefined) filters.headcount.min = headcountRangeFilter.min;
      if (headcountRangeFilter.max !== undefined) filters.headcount.max = headcountRangeFilter.max;
    }
    if (actualKcphRangeFilter) {
      filters.actualKcph = {};
      if (actualKcphRangeFilter.min !== undefined) filters.actualKcph.min = actualKcphRangeFilter.min;
      if (actualKcphRangeFilter.max !== undefined) filters.actualKcph.max = actualKcphRangeFilter.max;
    }
    if (forecastedHoursRangeFilter) {
      filters.forecastedHours = {};
      if (forecastedHoursRangeFilter.min !== undefined) filters.forecastedHours.min = forecastedHoursRangeFilter.min;
      if (forecastedHoursRangeFilter.max !== undefined) filters.forecastedHours.max = forecastedHoursRangeFilter.max;
    }
    if (forecastedCasesRangeFilter) {
      filters.forecastedCases = {};
      if (forecastedCasesRangeFilter.min !== undefined) filters.forecastedCases.min = forecastedCasesRangeFilter.min;
      if (forecastedCasesRangeFilter.max !== undefined) filters.forecastedCases.max = forecastedCasesRangeFilter.max;
    }
    if (actualCasesRangeFilter) {
      filters.actualCases = {};
      if (actualCasesRangeFilter.min !== undefined) filters.actualCases.min = actualCasesRangeFilter.min;
      if (actualCasesRangeFilter.max !== undefined) filters.actualCases.max = actualCasesRangeFilter.max;
    }
    
    console.log('Fetching schedule with filters:', filters);
    console.log('Range filter states:', {
      headcountRangeFilter,
      actualKcphRangeFilter,
      forecastedHoursRangeFilter,
      forecastedCasesRangeFilter,
      actualCasesRangeFilter
    });
    dispatch(fetchDashboardSchedule(filters) as any);
  }, [dispatch, yearFilter, periodFilter, weekFilter, createdByFilter, modifiedByFilter, 
      headcountRangeFilter, actualKcphRangeFilter, forecastedHoursRangeFilter, 
      forecastedCasesRangeFilter, actualCasesRangeFilter]);

  // Function to reset all filters and call API with only locationId
  const handleResetAllFilters = () => {
    console.log('Resetting all filters');
    // Clear all filter states
    setPeriodFilter(null);
    setWeekFilter(null);
    setYearFilter(null);
    setForecastedHoursFilter(null);
    setScheduledHoursFilter(null);
    setActualHoursFilter(null);
    setActualCasesFilter(null);
    setCreatedByFilter(null);
    setModifiedByFilter(null);
    
    // Clear range filter states
    setHeadcountRangeFilter(null);
    setActualKcphRangeFilter(null);
    setForecastedHoursRangeFilter(null);
    setForecastedCasesRangeFilter(null);
    setActualCasesRangeFilter(null);
    
    // Let useEffect handle the API call with cleared filters
    console.log('All filters reset - useEffect will handle API call');
  };

  // Filter change handlers - let useEffect handle all API calls
  const handlePeriodFilterChange = (option: DLODropdownOption | null) => {
    console.log('Period filter changed:', option);
    setPeriodFilter(option);
    // Let useEffect handle the API call to avoid duplicates
  };

  const handleWeekFilterChange = (option: DLODropdownOption | null) => {
    console.log('Week filter changed:', option);
    setWeekFilter(option);
    // Let useEffect handle the API call to avoid duplicates
  };

  const handleYearFilterChange = (option: DLODropdownOption | null) => {
    console.log('Year filter changed:', option);
    setYearFilter(option);
    // Let useEffect handle the API call to avoid duplicates
  };

  const handleCreatedByFilterChange = (option: DLODropdownOption | null) => {
    console.log('Created By filter changed:', option);
    setCreatedByFilter(option);
    // Let useEffect handle the API call to avoid duplicates
  };

  const handleModifiedByFilterChange = (option: DLODropdownOption | null) => {
    console.log('Modified By filter changed:', option);
    setModifiedByFilter(option);
    // Let useEffect handle the API call to avoid duplicates
  };

  const handleForecastedHoursFilterChange = (option: DLODropdownOption | null) => {
    setForecastedHoursFilter(option);
    // No API call needed for non-API filters
  };

  const handleScheduledHoursFilterChange = (option: DLODropdownOption | null) => {
    setScheduledHoursFilter(option);
    // No API call needed for non-API filters
  };

  const handleActualHoursFilterChange = (option: DLODropdownOption | null) => {
    setActualHoursFilter(option);
    // No API call needed for non-API filters
  };

  const handleActualCasesFilterChange = (option: DLODropdownOption | null) => {
    setActualCasesFilter(option);
    // No API call needed for non-API filters
  };

  // Range filter change handlers
  const handleHeadcountRangeFilterChange = (value: RangeFilterValue | null) => {
    console.log('Headcount range filter changed:', value);
    setHeadcountRangeFilter(value);
    // Let useEffect handle the API call to avoid duplicates
  };

  const handleActualKcphRangeFilterChange = (value: RangeFilterValue | null) => {
    console.log('Actual KCPH range filter changed:', value);
    setActualKcphRangeFilter(value);
    // Let useEffect handle the API call to avoid duplicates
  };

  const handleForecastedHoursRangeFilterChange = (value: RangeFilterValue | null) => {
    console.log('Forecasted Hours range filter changed:', value);
    setForecastedHoursRangeFilter(value);
    // Let useEffect handle the API call to avoid duplicates
  };

  const handleForecastedCasesRangeFilterChange = (value: RangeFilterValue | null) => {
    console.log('Forecasted Cases range filter changed:', value);
    setForecastedCasesRangeFilter(value);
    // Let useEffect handle the API call to avoid duplicates
  };

  const handleActualCasesRangeFilterChange = (value: RangeFilterValue | null) => {
    console.log('Actual Cases range filter changed:', value);
    setActualCasesRangeFilter(value);
    // Let useEffect handle the API call to avoid duplicates
  };

  // Ref to track if filters have been initialized (avoids duplicate API call on mount).
  // useRef is intentional here — mutating .current does NOT trigger a re-render,
  // which prevents a second fetchDashboardSchedule call right after the mount useEffect.
  const hasFiltersInitialized = useRef(false);

  // Effect to trigger API call only when filters actually change (not on initial mount)
  useEffect(() => {
    if (!hasFiltersInitialized.current) {
      hasFiltersInitialized.current = true;
      return;
    }
    console.log('Filter change detected, calling API...');
    fetchScheduleWithFilters();
  }, [fetchScheduleWithFilters]);

  // Effect to reset pagination when schedule data changes
  useEffect(() => {
    // Reset to first page when data changes
    setCurrentPage(1);
  }, [scheduleData]);

  // Debug effect to track loading state changes
  useEffect(() => {
    console.log('KCPH Loading state changed:', kcphLoading);
    console.log('KCPH Error state:', kcphError);
    console.log('KCPH Data:', kcphData);
    console.log('Hours Loading state changed:', hoursLoading);
    console.log('Hours Error state:', hoursError);
    console.log('Hours Data:', hoursData);
    console.log('Schedule Loading state changed:', scheduleLoading);
    console.log('Schedule Error state:', scheduleError);
    console.log('Schedule Data:', scheduleData);
  }, [kcphLoading, kcphError, kcphData, hoursLoading, hoursError, hoursData, scheduleLoading, scheduleError, scheduleData]);

  // Function to transform KCPH API data to chart format
  const transformKCPHToChartData = (): HorizontalChartDataPoint[] => {
    if (!kcphData) {
      console.log('No KCPH data available');
      return [];
    }

    console.log('Raw KCPH data:', kcphData);

    const chartData: HorizontalChartDataPoint[] = [];

    // Add data points only if they have valid values
    if (kcphData.scheduledKcph !== null && kcphData.scheduledKcph !== undefined) {
      chartData.push({
        label: 'Scheduled\nKCPH',
        value: kcphData.scheduledKcph,
        color: '#F4C430' // Yellow/Gold color
      });
    }

    if (kcphData.estimatedKcph !== null && kcphData.estimatedKcph !== undefined) {
      chartData.push({
        label: 'Estimated\nKCPH',
        value: kcphData.estimatedKcph,
        color: '#33ABDA' // Blue color
      });
    }

    if (kcphData.actualKcph !== null && kcphData.actualKcph !== undefined) {
      chartData.push({
        label: 'Actual\nKCPH',
        value: kcphData.actualKcph,
        color: '#B05788' // Purple/Pink color
      });
    }

    console.log('Transformed chart data:', chartData);
    return chartData;
  };

  // Helper function to extract favorite schedule data from scheduleTableData
  const extractFavoriteScheduleData = (favoriteId: string) => {
    if (!scheduleData?.data || !favoriteId) {
      return null;
    }

    // Find the favorite schedule from the table data
    const favoriteSchedule = scheduleData.data.find((record: any) => {
      const recordId = `fav-${record["Loc ID"]}-${record.YR}-${record.PRD}-${record.WK}`;
      return favoriteId.includes(recordId) || favoriteId.includes(record.fav_name);
    });

    if (!favoriteSchedule) {
      console.warn('Favorite schedule not found:', favoriteId);
      return null;
    }

    return {
      period: favoriteSchedule.PRD,
      week: favoriteSchedule.WK,
      year: favoriteSchedule.YR,
      locationId: favoriteSchedule["Loc ID"]
    };
  };

  // Calculate chart range based on data
  const getChartRange = () => {
    const chartData = transformKCPHToChartData();
    const targetValue = kcphData?.targetKcph || 0;
    
    if (chartData.length === 0 && !targetValue) {
      return { minValue: 0, maxValue: 100 };
    }

    const allValues = [...chartData.map(d => d.value), targetValue].filter(v => v !== null && v !== undefined);
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    
    // Add some padding to the range
    const padding = (max - min) * 0.1 || 10;
    return {
      minValue: Math.max(0, min - padding),
      maxValue: max + padding
    };
  };

  // Function to transform Dashboard Hours API data to bar chart format
  const transformHoursToChartData = (): ChartDataPoint[] => {
    if (!hoursData || !hoursData.data) {
      console.log('No Dashboard Hours data available');
      return [];
    }

    console.log('Raw Dashboard Hours data:', hoursData);

    // Map day numbers to day names (1=Sunday, 7=Saturday)
    const dayNames = ['', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Create a map from API data by dayNumber
    const dataByDay = new Map();
    hoursData.data.forEach((dayData: any) => {
      dataByDay.set(dayData.dayNumber, dayData);
    });

    // Create chart data for all 7 days (1=Sunday through 7=Saturday)
    const chartData: ChartDataPoint[] = [];
    for (let dayNum = 1; dayNum <= 7; dayNum++) {
      const dayData = dataByDay.get(dayNum);
      
      chartData.push({
        day: dayNames[dayNum] || '',
        estimatedHours: dayData?.totalEstimatedHours || 0, // Use totalEstimatedHours from API
        actualHours: dayData?.totalActualHours || 0, // Use 0 if null/undefined
      });
    }

    console.log('Transformed bar chart data:', chartData);
    return chartData;
  };

  // Calculate max value for the bar chart
  const getBarChartMaxValue = (): number => {
    const chartData = transformHoursToChartData();
    if (chartData.length === 0) {
      return 50; // Default max value
    }

    const maxValue = Math.max(
      ...chartData.map(d => Math.max(d.estimatedHours, d.actualHours))
    );

    // Round up to nearest 10 for better Y-axis labels
    return Math.ceil(maxValue / 10) * 10;
  };

  // Function to transform Dashboard Schedule API data to table format
  // Helper to get the correct schedule data (filtered or base)
  const getActiveScheduleData = () => {
    // Determine if any filter is selected (including range filters)
    const isAnyFilterSelected =
      yearFilter || periodFilter || weekFilter || createdByFilter || modifiedByFilter ||
      headcountRangeFilter || actualKcphRangeFilter || forecastedHoursRangeFilter || 
      forecastedCasesRangeFilter || actualCasesRangeFilter;

    if (isAnyFilterSelected) {
      // If any filter is selected, always use filteredScheduleData
      return filteredScheduleData;
    }
    return scheduleData;
  };

  const transformScheduleToTableData = (): ScheduleRowData[] => {
    const activeData = getActiveScheduleData();
    if (!activeData?.data) {
      console.log('No schedule data available');
      return [];
    }

    console.log('Raw Dashboard Schedule data:', activeData);

    // Get global KCPH data from Redux
    const kcph = kcphData || {};

    return activeData.data.map((record: any) => {
      // Calculate actual cases percentage if both values exist
      let actualCasesDisplay = '--';
      let actualCasesColor: 'error' | 'success' | 'default' = 'default';
      
      if (record.total_forecasted_cases && record.Total_Actual_Cases !== null) {
        const percentageDiff = ((record.Total_Actual_Cases - record.total_forecasted_cases) / record.total_forecasted_cases) * 100;
        actualCasesDisplay = `${record.Total_Actual_Cases.toLocaleString()} (${percentageDiff >= 0 ? '+' : ''}${percentageDiff.toFixed(1)}%)`;
        actualCasesColor = percentageDiff >= 0 ? 'success' : 'error';
      }

      // Extract emails and format names
      const createdByEmail = record.CreatedBy || '';
      const modifiedByEmail = record.ModifiedBy || '';

      // Map backend week to UI period/week
      const { period, week } = fromBackendWeek(Number(record.WK));

      // Status logic
      const originalStatus = record.Status || '';
      let uiStatus = originalStatus;
      const currentPeriodWeek = periodWeekData || {};
      const currentPeriod = Number(currentPeriodWeek.period);
      const currentWeek = Number(toBackendWeek(currentPeriodWeek.period, currentPeriodWeek.week));
      const currentYear = new Date().getFullYear();
      const entryYear = Number(record.YR);
      const entryPeriod = Number(record.PRD);
      const entryWeek = Number(record.WK);
      // Helper: is entry in future?
      const isFuture = entryYear > currentYear || (entryYear === currentYear && (entryPeriod > currentPeriod || (entryPeriod === currentPeriod && entryWeek > currentWeek)));
      if (originalStatus.toLowerCase() === 'closed') {
        console.log(`Determining UI status for closed entry: Entry YR:${entryYear}, PRD:${entryPeriod}, WK:${entryWeek} vs Current YR:${currentYear}, PRD:${currentPeriod}, WK:${currentWeek}`);
        if (
          entryYear === currentYear &&
          entryPeriod === currentPeriod &&
          entryWeek === currentWeek
        ) {
          uiStatus = 'Active';
        } else if (entryYear < currentYear || (entryYear === currentYear && (entryPeriod < currentPeriod || (entryPeriod === currentPeriod && entryWeek < currentWeek)))) {
          uiStatus = 'Closed';
        } else if (isFuture) {
          uiStatus = 'Finalized';
        } else {
          uiStatus = 'Deleted';
        }
      } else if (originalStatus.toLowerCase() === 'in progress') {
        if (isFuture) {
          uiStatus = 'Scheduled';
        }
        else if(
          entryYear === currentYear &&
          entryPeriod === currentPeriod &&
          entryWeek === currentWeek
        ) {
          uiStatus = 'Active';
        }
      }

      // Add ACTUAL HOURS field (safe toString)
      const actualHours = record.total_actual_hours !== null && record.total_actual_hours !== undefined
        ? record.total_actual_hours.toString()
        : '--';

      // Add SCHEDULED HOURS field (safe toString)
      const scheduledHours = record.total_scheduled_hours !== null && record.total_scheduled_hours !== undefined
        ? record.total_scheduled_hours.toString()
        : '--';

      // Safe toString for year, period, week
      const yearStr = record.YR !== null && record.YR !== undefined ? record.YR.toString() : '--';
      const periodStr = period !== null && period !== undefined ? period.toString() : '--';
      const weekStr = week !== null && week !== undefined ? week.toString() : '--';

      return {
        id: `${record["Loc ID"]}-${yearStr}-${periodStr}-${record.WK}`,
        period: periodStr,
        week: weekStr,
        year: yearStr,
        forecastedCases: record.total_forecasted_cases ? record.total_forecasted_cases.toLocaleString() : '--',
        actualCases: actualCasesDisplay,
        actualCasesColor,
        actualHours, // <-- ACTUAL HOURS header value
        createdBy: createdByEmail ? formatUserName(createdByEmail) : 'Unknown',
        modifiedBy: modifiedByEmail ? formatUserName(modifiedByEmail) : 'Unknown',
        createdByEmail,
        modifiedByEmail,
        recommendedHours: record.recommended_hours ? record.recommended_hours.toFixed(1) : '--',
        scheduledHours,
        estimatedHours: record.estimated_hours ? record.estimated_hours.toFixed(1) : '--',
        isSelected: false,
        isFavorite: record.is_fav,
        fav_name: record.fav_name, // Include favorite name from API response
        // Additional fields for API calls
        periodNumber: period,
        weekNumber: week,
        yearNumber: record.YR,
        locId: record["Loc ID"],
        backendWeek: record.WK,
        status: originalStatus,
        uiStatus,
        forecastedKcph: record.forecastedKcph != null ? Number(record.forecastedKcph).toFixed(1) : (kcph.forecastedKcph != null ? Number(kcph.forecastedKcph).toFixed(1) : '--'),
        scheduledKcph: record.scheduledKcph != null ? Number(record.scheduledKcph).toFixed(1) : (kcph.scheduledKcph != null ? Number(kcph.scheduledKcph).toFixed(1) : '--'),
        estimatedKcph: record.estimatedKcph != null ? Number(record.estimatedKcph).toFixed(1) : (kcph.estimatedKcph != null ? Number(kcph.estimatedKcph).toFixed(1) : '--'),
        actualKcph: record.actualKcph != null ? Number(record.actualKcph).toFixed(1) : (kcph.actualKcph != null ? Number(kcph.actualKcph).toFixed(1) : '--'),
        targetKcph: record.targetKcph != null ? Number(record.targetKcph).toFixed(1) : (kcph.targetKcph != null ? Number(kcph.targetKcph).toFixed(1) : '--'),
        headcount: record.headcount != null ? Math.round(Number(record.headcount)).toString() : (kcph.headcount != null ? Math.round(Number(kcph.headcount)).toString() : '--'),
        directEstimated: record.DSD_estimated_hours != null ? Number(record.DSD_estimated_hours).toFixed(1) : '--',
        directForecasted: record.DSD_recommended_hours != null ? Number(record.DSD_recommended_hours).toFixed(1) : '--',
        directActual: record.DSD_total_actual_hours != null ? Number(record.DSD_total_actual_hours).toFixed(1) : '--',
        directScheduled: record.DSD_total_scheduled_hours != null ? Number(record.DSD_total_scheduled_hours).toFixed(1) : '--',
        nonDirectEstimated: record.NONDSD_estimated_hours != null ? Number(record.NONDSD_estimated_hours).toFixed(1) : '--',
        nonDirectForecasted: record.NONDSD_recommended_hours != null ? Number(record.NONDSD_recommended_hours).toFixed(1) : '--',
        nonDirectActual: record.NONDSD_total_actual_hours != null ? Number(record.NONDSD_total_actual_hours).toFixed(1) : '--',
        nonDirectScheduled: record.NONDSD_total_scheduled_hours != null ? Number(record.NONDSD_total_scheduled_hours).toFixed(1) : '--',
        otherEstimated: record.OTHER_estimated_hours != null ? Number(record.OTHER_estimated_hours).toFixed(1) : '--',
        otherForecasted: record.OTHER_recommended_hours != null ? Number(record.OTHER_recommended_hours).toFixed(1) : '--',
        otherActual: record.OTHER_total_actual_hours != null ? Number(record.OTHER_total_actual_hours).toFixed(1) : '--',
        otherScheduled: record.OTHER_total_scheduled_hours != null ? Number(record.OTHER_total_scheduled_hours).toFixed(1) : '--',
        differenceofHours: record.recommended_hours != null && record.total_scheduled_hours != null
          ? Number(record.recommended_hours) - Number(record.total_scheduled_hours)
          : 0,
      };
    });
  };

  // Bar Chart Loading Component
  const BarChartLoadingIndicator = () => (
    <View style={styles.chartLoadingContainer}>
      <DLOBarChart
        title="Performance by Day of Week"
        data={[]}
        legend={[
          { label: 'Adjusted Recommended Hours', color: '#33ABDA' },
          { label: 'Actual Hours', color: '#B05788' },
        ]}
        maxValue={50}
      />
      <View style={styles.chartLoadingOverlay}>
        <ActivityIndicator size="large" color="#0066CC" />
        <Text style={styles.loadingText}>Loading hours data...</Text>
      </View>
    </View>
  );

  // Bar Chart Error Component
  const BarChartErrorIndicator = () => (
    <View style={styles.chartErrorContainer}>
      <DLOBarChart
        title="Performance by Day of Week"
        data={[]}
        legend={[
          { label: 'Adjusted Recommended Hours', color: '#33ABDA' },
          { label: 'Actual Hours', color: '#B05788' },
        ]}
        maxValue={50}
      />
      <View style={styles.chartErrorOverlay}>
        <Text style={styles.errorText}>Failed to load hours data</Text>
        <Text style={styles.errorSubText}>Please try again</Text>
      </View>
    </View>
  );

  // Chart Loading Component
  const ChartLoadingIndicator = () => (
    <View style={styles.chartLoadingContainer}>
      <DLOHorizontalBarChart
        title="Efficiency Metrics"
        data={[]}
        targetValue={0}
        targetLabel="Target KCPH"
        minValue={0}
        maxValue={100}
      />
      <View style={styles.chartLoadingOverlay}>
        <ActivityIndicator size="large" color="#0066CC" />
        <Text style={styles.loadingText}>Loading KCPH data...</Text>
      </View>
    </View>
  );

  // Chart Error Component
  const ChartErrorIndicator = () => (
    <View style={styles.chartErrorContainer}>
      <DLOHorizontalBarChart
        title="Efficiency Metrics"
        data={[]}
        targetValue={0}
        targetLabel="Target KCPH"
        minValue={0}
        maxValue={100}
      />
      <View style={styles.chartErrorOverlay}>
        <Text style={styles.errorText}>Failed to load KCPH data</Text>
        <Text style={styles.errorSubText}>Please try again</Text>
      </View>
    </View>
  );

  // Loader state for copy-schedule API
  const [copyScheduleLoading, setCopyScheduleLoading] = useState(false);
  // Loader state for delete-schedule API
  const [deleteScheduleLoading, setDeleteScheduleLoading] = useState(false);
  // Handler functions for table interactions
  // Enforce single selection: only one row can be selected at a time
  const handleRowSelect = (id: string, selected: boolean) => {
    if (selected) {
      setSelectedRowIds(new Set([id]));
      setSelectedCount(1);
      // Find and set the selected schedule data
      const allTableData = getAllTableData();
      const selectedSchedule = allTableData.find(row => row.id === id);
      setSelectedScheduleData(selectedSchedule || null);
    } else {
      setSelectedRowIds(new Set());
      setSelectedCount(0);
      setSelectedScheduleData(null);
    }
  };

  const handleRowFavorite = (id: string, favorite: boolean) => {
    console.log(`Row ${id} ${favorite ? 'favorited' : 'unfavorited'}`);
  };

  const handleHeaderSort = (column: string) => {
    // Toggle sort direction if same column, else default to ascending
    setSortColumn(prev => {
      if (prev === column) {
        setSortDirection(dir => (dir === 'asc' ? 'desc' : 'asc'));
        return prev;
      } else {
        setSortDirection('asc');
        return column;
      }
    });
  };

  // Header action handlers
  const handleEdit = async () => {
    if (selectedCount === 1 && selectedScheduleData) {
      // Set period, week, and year in Redux
      const period = selectedScheduleData.periodNumber || parseInt(selectedScheduleData.period, 10);
      const week = selectedScheduleData.weekNumber || parseInt(selectedScheduleData.week, 10);
      const year = selectedScheduleData.yearNumber || parseInt(selectedScheduleData.year, 10);

      const schedulePeriodWeekData = {
        period,
        week: toBackendWeek(period, week),
        year
      };
      dispatch(setCreateSchedulePeriodWeek(schedulePeriodWeekData));

      // Set loading state
      setCopyScheduleLoading(true);

      try {
        // Call cleanScheduleSettings BEFORE navigating to ensure lock is acquired
        const cleanSettingsResult = await dispatch(cleanScheduleSettings({
          locId: currentLocation?.id || '',
          period: period,
          week: toBackendWeek(period, week),
          year: year
        }) as any);

        // Check if cleanScheduleSettings failed
        if (!cleanSettingsResult.payload) {
          console.error('cleanScheduleSettings failed - not navigating');
          // Lock error popup will be shown by interceptors, stay on dashboard
          return;
        }

        console.log('cleanScheduleSettings succeeded, proceeding with navigation');

        // Determine if selected schedule is for the current week
        const currentPeriod = periodWeekData?.period;
        const currentWeek = toBackendWeek(periodWeekData?.period, periodWeekData?.week);
        const selectedPeriod = period;
        const selectedWeek = toBackendWeek(period, week);
        // Use Redux selector for current period/week
        // periodWeekData from Redux: { period, week }
        const isCurrentWeek = (selectedPeriod === currentPeriod && selectedWeek === currentWeek);
        if (onNavigate) {
          if (isCurrentWeek) {
            try {
              onNavigate('DLOScheduleSetting', { activeTab: 'actual', scheduleType: 'existing_schedule' });
              console.log('Edit: Navigation call completed');
            } catch (error) {
              console.error('Edit: Navigation error:', error);
            }
          } else {
            onNavigate('DLOScheduleSetting', { scheduleType: 'existing_schedule' });
            console.log('Edit: Selected schedule is not for current week, navigating to actual tab.');
          }
        } else {
          console.warn('Edit: onNavigate prop is not available - navigation not possible');
        }
      } finally {
        setCopyScheduleLoading(false);
      }
    } else {
      console.log(`Edit ${selectedCount} item(s)`);
    }
  };

  const handleCopySchedule = () => {
    if (selectedCount > 0) {
      console.log(`Copy ${selectedCount} schedule(s)`);
    }
  };

  const handleDelete = async () => {
    setDeleteError(null);
    setDeleteSuccess(null);
    if (selectedCount === 1 && selectedScheduleData) {
      setDeleteScheduleLoading(true);
      console.log('Delete schedule:', selectedScheduleData);
      if (selectedScheduleData.uiStatus === 'Scheduled') {
        // Call DELETE API for future schedule
        const payload = {
          year: Number(selectedScheduleData.year),
          period: Number(selectedScheduleData.period),
          week: Number(selectedScheduleData.backendWeek),
          locId: Number(selectedScheduleData.locId)
        };

        // Acquire lock before delete, then release after
        let lockService = getSessionLockService();
        if (!lockService) {
          lockService = initializeSessionLockService(api);
        }
        let acquiredResourceId: string | null = null;
        let acquiredLockToken: string | null = null;

        try {
          // Acquire lock for the schedule resource
          const lockResponse = await lockService.acquireLock({
            module: 'DLO',
            submodule: 'Schedule',
            locId: String(payload.locId),
            period: payload.period,
            week: payload.week,
            year: payload.year,
            sessionId: '',
          });
          acquiredResourceId = lockResponse.resourceId || null;
          acquiredLockToken = lockResponse.lockToken || null;
          console.log('[DLODashboard] Lock acquired for delete:', acquiredResourceId);

          // Add lock token to payload
          const deletePayload = { ...payload, lockToken: acquiredLockToken };

          await apiClient(api, "/dlo/schedule/future", "DELETE", deletePayload);
          dispatch(fetchDashboardSchedule({}) as any);
          setSelectedCount(0);
          const period = selectedScheduleData.period;
          const week = selectedScheduleData.week;
          const year = selectedScheduleData.year;
          setDeleteSuccess(`Successfully deleted ${period}-${week}-${year} schedule.`);
        } catch (error: any) {
          console.error('Error deleting schedule:', error);
          if (error?.isLockConflict) {
            setDeleteError('Schedule is currently locked by another user. Please try again later.');
          } else {
            setDeleteError('Failed to delete schedule. Please try again.');
          }
        } finally {
          // Always release the lock after delete operation
          if (lockService && acquiredResourceId && acquiredLockToken) {
            try {
              await lockService.releaseLock(acquiredResourceId, acquiredLockToken);
              console.log('[DLODashboard] Lock released after delete:', acquiredResourceId);
            } catch (releaseError) {
              console.error('[DLODashboard] Error releasing lock after delete:', releaseError);
            }
          }
          setDeleteScheduleLoading(false);
        }
      } else {
        setDeleteScheduleLoading(false);
        // Show error: cannot delete in current state
        setDeleteError(`Schedules for past or current week cannot be deleted. Only future schedules which are not finalized can be deleted.`);
      }
    } else {
      setDeleteScheduleLoading(false);
      // Show error: only single selection allowed
      setDeleteError('Please select a single schedule with status "Scheduled" to delete.');
    }
  };

  const handleCreateSchedule = () => {
    console.log('Create Schedule button clicked from dashboard');
    // Open the Create Schedule modal instead of navigating directly
    setShowCreateScheduleModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateScheduleModal(false);
  };

  const handleCopyScheduleFromModal = async (data: CreateScheduleData) => {
    console.log('Copy Schedule from modal:', data);
    // Close the modal immediately when button is clicked
    setShowCreateScheduleModal(false);
    setCopyScheduleLoading(true);
    try {
      // Store Period, Week, and Year values in DLO Redux state
      const periodValue = parseInt(data.period!.value, 10);
      const weekValue = parseInt(data.week!.value, 10);
      const yearValue = parseInt(data.year!.value, 10);

      if (data.period && data.week && data.year) {
        const periodWeekData = {
          period: periodValue,
          week: (periodValue - 1) * 4 + weekValue,
          year: yearValue
        };
        console.log('Dispatching setCreateSchedulePeriodWeek with:', periodWeekData);
        dispatch(setCreateSchedulePeriodWeek(periodWeekData));
      }

      // Call cleanScheduleSettings BEFORE navigating to ensure lock is acquired
      const cleanSettingsResult = await dispatch(cleanScheduleSettings({
        locId: currentLocation?.id || '',
        period: periodValue,
        week: (periodValue - 1) * 4 + weekValue,
        year: yearValue
      }) as any);

      // Check if cleanScheduleSettings failed
      if (!cleanSettingsResult.payload) {
        console.error('cleanScheduleSettings failed - not navigating');
        // Lock error popup will be shown by interceptors, stay on dashboard
        return;
      }

      console.log('cleanScheduleSettings succeeded, proceeding with navigation');

      // Check if a favorite is selected
      if (data.favorite) {
        console.log('Favorite selected, calling copy-schedule API...', data.favorite.value);
        // Extract favorite schedule data
        const favoriteData = extractFavoriteScheduleData(data.favorite.value);
        if (!favoriteData) {
          console.error('Could not extract favorite schedule data');
          // Still navigate to create schedule page even if copy fails
        } else {
          // Prepare copy-schedule API payload
          const copyPayload = {
            targetPeriod: periodValue,
            targetWeek: toBackendWeek(periodValue, weekValue),
            targetYear: yearValue,
            targetLocationId: currentLocation?.id || '',
            sourcePeriod: favoriteData.period,
            sourceWeek: favoriteData.week,
            sourceYear: favoriteData.year,
            sourceLocationId: favoriteData.locationId
          };
          console.log('Copy Schedule API payload:', copyPayload);
          try {
            // Call the copy-schedule API
            await dispatch(copySchedule(copyPayload) as any);
            console.log('Copy Schedule API call successful');
          } catch (error) {
            console.error('Copy Schedule API call failed:', error);
            // Continue with navigation even if copy fails
          }
        }
      }

      // Navigate to the Create Schedule page
      if (onNavigate) {
        console.log('Navigating to DLOScheduleSetting...');
        try {
          onNavigate('DLOScheduleSetting', { scheduleType: 'new_schedule' });
          console.log('Navigation call completed');
        } catch (error) {
          console.error('Navigation error:', error);
        }
      } else {
        console.warn('onNavigate prop is not available - navigation not possible');
      }
    } finally {
      setCopyScheduleLoading(false);
    }
  };

  // Copy Schedule from Table handler - handles API call and navigation for table-selected schedules
  const handleCopyScheduleFromTable = async (targetPeriod: number, targetWeek: number, targetYear: number, selectedSchedule: any) => {
    console.log('Copy Schedule from table:', {
      targetPeriod,
      targetWeek,
      targetYear,
      selectedSchedule
    });
    setCopyScheduleLoading(true);
    try {
      if (!selectedSchedule) {
        console.error('No selected schedule data available');
        return;
      }
      const calculatedWeek = toBackendWeek(targetPeriod, targetWeek);
      
      // Call cleanScheduleSettings BEFORE navigating to ensure lock is acquired
      const cleanSettingsResult = await dispatch(cleanScheduleSettings({
        locId: currentLocation?.id || '',
        period: targetPeriod,
        week: calculatedWeek,
        year: targetYear
      }) as any);

      // Check if cleanScheduleSettings failed
      if (!cleanSettingsResult.payload) {
        console.error('cleanScheduleSettings failed - not navigating');
        // Lock error popup will be shown by interceptors, stay on dashboard
        return;
      }

      console.log('cleanScheduleSettings succeeded, proceeding with navigation');

      // Store Period, Week, and Year values in DLO Redux state
      const periodWeekData = {
        period: targetPeriod,
        week: calculatedWeek,
        year: targetYear
      };
      console.log('Dispatching setCreateSchedulePeriodWeek with:', periodWeekData);
      dispatch(setCreateSchedulePeriodWeek(periodWeekData));
      // Prepare copy-schedule API payload using selected schedule as source
      const copyPayload = {
        targetPeriod: targetPeriod,
        targetWeek: calculatedWeek,
        targetYear: targetYear,
        targetLocationId: currentLocation?.id || '',
        // Use selected schedule data as source (favorite fields)
        sourcePeriod: selectedSchedule.periodNumber || parseInt(selectedSchedule.period),
        sourceWeek: toBackendWeek(selectedSchedule.periodNumber || parseInt(selectedSchedule.period), selectedSchedule.weekNumber || parseInt(selectedSchedule.week)),
        sourceYear: selectedSchedule.yearNumber || parseInt(selectedSchedule.year),
        sourceLocationId: selectedSchedule.locId || currentLocation?.id || ''
      };
      console.log('Copy Schedule from table API payload:', copyPayload);
      try {
        // Call the copy-schedule API
        await dispatch(copySchedule(copyPayload) as any);
        console.log('Copy Schedule from table API call successful');
      } catch (error) {
        console.error('Copy Schedule from table API call failed:', error);
        // Continue with navigation even if copy fails
      }
      // Navigate to the Create Schedule page
      if (onNavigate) {
        console.log('Navigating to DLOScheduleSetting...');
        try {
          onNavigate('DLOScheduleSetting', { scheduleType: 'new_schedule' });
          console.log('Navigation call completed');
        } catch (error) {
          console.error('Navigation error:', error);
        }
      } else {
        console.warn('onNavigate prop is not available - navigation not possible');
      }
    } finally {
      setCopyScheduleLoading(false);
    }
  };

  const handleLocationSettings = () => {
    console.log('Location Settings pressed from dashboard');
    // Navigate to DLOLocationSetting page
    onNavigate?.('DLOLocationSetting');
  };

  // Chart filter change handler
  const handleChartFiltersChange = (filters: ChartFilterValue) => {
    setChartFilters(filters);
    console.log('Chart filters changed:', filters);
    console.log('Refetching KCPH, Dashboard Hours, and Dashboard Schedule data due to filter change...');
    
    // Extract filter values and pass to API calls
    const chartFilterParams = {
      period: filters.period ? parseInt(filters.period.value) : undefined,
      week: filters.week ? parseInt(filters.week.value) : undefined,
      year: filters.year ? parseInt(filters.year.value) : undefined,
    };
    
    console.log('Chart filter parameters for API calls:', chartFilterParams);
    
    // Refetch all APIs when filters change with new parameters
    dispatch(fetchKCPHData(chartFilterParams) as any);
    dispatch(fetchDashboardHours(chartFilterParams) as any);
    // dispatch(fetchDashboardSchedule({}) as any);
  };

  // Filter modal handlers
  const handleOpenFilterModal = () => {
    setTempFilters(chartFilters);
    setShowFilterModal(true);
  };

  const handleCloseFilterModal = () => {
    setShowFilterModal(false);
  };

  const handleApplyFilters = () => {
    handleChartFiltersChange(tempFilters);
    setShowFilterModal(false);
  };

  const handleTempFilterChange = (filterType: 'period' | 'week' | 'year', option: DLODropdownOption | DLODropdownOption[] | null) => {
    const selectedOption = Array.isArray(option) ? option[0] : option;
    setTempFilters(prev => ({
      ...prev,
      [filterType]: selectedOption,
    }));
  };

  // Generate filter display text
  const getFilterDisplayText = () => {
    const parts = [];
    if (chartFilters.period) {
      parts.push(`P${chartFilters.period.value}`);
    }
    if (chartFilters.week) {
      parts.push(`W${chartFilters.week.value}`);
    }
    if (chartFilters.year) {
      parts.push(chartFilters.year.value);
    }
    return parts.join(' ');
  };

  return (
    <View style={{ flex: 1 }}>
      {/* Loader overlay for copy-schedule API */}
      {(copyScheduleLoading || deleteScheduleLoading) && (
        <View style={styles.fullScreenLoaderOverlay}>
          <ActivityIndicator size="large" color="#0066CC" />
        </View>
      )}
      <ScrollView style={styles.container} pointerEvents={copyScheduleLoading ? 'none' : 'auto'}>
      {/* Dashboard Title Header */}
      <View style={styles.headerContainer}>
        <DLOLaborOptimizationTitle
          onCreateSchedule={handleCreateSchedule}
          onLocationSettings={handleLocationSettings}
          isCreatingSchedule={false}
        />
      </View>

      {/* Chart - Top Section */}
      <View style={styles.chartContainer}>
        {/* Chart Filters */}
        <View style={styles.filtersContainer}>
          <View style={styles.filterRow}>
            {/* Display current filter values */}
            <Text style={styles.filterDisplayText}>{getFilterDisplayText()}</Text>
            
            {/* Filters Button */}
            <TouchableOpacity style={styles.filtersButton} onPress={handleOpenFilterModal}>
              <Text style={styles.filtersButtonText}>Filters</Text>
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.chartsRow}>
          {/* Performance Chart - Bar Chart (Left) */}
          <View style={styles.chartLeft}>
            {hoursLoading ? (
              <BarChartLoadingIndicator />
            ) : hoursError ? (
              <BarChartErrorIndicator />
            ) : !hoursData || !hoursData.data || hoursData.data.length === 0 ? (
              <DLOBarChart
                title="Performance by Day of Week"
                data={[]}
                legend={[
                  { label: 'Adjusted Recommended Hours', color: '#33ABDA' },
                  { label: 'Actual Hours', color: '#B05788' },
                ]}
                maxValue={getBarChartMaxValue()}
              />
            ) : (
              <DLOBarChart
                title="Performance by Day of Week"
                data={transformHoursToChartData()}
                legend={[
                  { label: 'Adjusted Recommended Hours', color: '#33ABDA' },
                  { label: 'Actual Hours', color: '#B05788' },
                ]}
                maxValue={getBarChartMaxValue()}
              />
            )}
          </View>
          
          {/* Efficiency Metrics Chart - Horizontal Bar Chart (Right) */}
          <View style={styles.chartRight}>
            {kcphLoading ? (
              <ChartLoadingIndicator />
            ) : kcphError ? (
              <ChartErrorIndicator />
            ) : (
              <DLOHorizontalBarChart
                title="Efficiency Metrics"
                data={transformKCPHToChartData()}
                targetValue={kcphData?.targetKcph || 0}
                targetLabel="Target KCPH"
                minValue={getChartRange().minValue}
                maxValue={getChartRange().maxValue}
              />
            )}
          </View>
        </View>
      </View>

      {/* Complete Schedule Table Section */}
      <View style={styles.scheduleTableContainer}>
        <View style={styles.scheduleTableWrapper}>
          {/* Delete error message above table */}
          {deleteSuccess && (
            <View style={{ backgroundColor: '#E6FFEA', padding: 12, marginTop: 8, borderRadius: 6, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={{ color: '#1A7F37', fontWeight: '600', fontSize: 14, flex: 1 }}>{deleteSuccess}</Text>
              <TouchableOpacity onPress={() => setDeleteSuccess(null)} style={{ marginLeft: 12, padding: 4 }}>
                <Text style={{ color: '#1A7F37', fontWeight: 'bold', fontSize: 18 }}>×</Text>
              </TouchableOpacity>
            </View>
          )}
          {deleteError && (
            <View style={{ backgroundColor: '#FFEAEA', padding: 12, marginTop: 8, borderRadius: 6, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={{ color: '#D40020', fontWeight: '600', fontSize: 14, flex: 1 }}>{deleteError}</Text>
              <TouchableOpacity onPress={() => setDeleteError(null)} style={{ marginLeft: 12, padding: 4 }}>
                <Text style={{ color: '#D40020', fontWeight: 'bold', fontSize: 18 }}>×</Text>
              </TouchableOpacity>
            </View>
          )}
          <DLOScheduleTableHeader
            selectedCount={selectedCount}
            tableData={getAllTableData()}
            selectedScheduleData={selectedScheduleData}
            // Filter values
            periodFilter={periodFilter}
            weekFilter={weekFilter}
            yearFilter={yearFilter}
            forecastedHoursFilter={forecastedHoursFilter}
            scheduledHoursFilter={scheduledHoursFilter}
            actualHoursFilter={actualHoursFilter}
            actualCasesFilter={actualCasesFilter}
            createdByFilter={createdByFilter}
            modifiedByFilter={modifiedByFilter}
            // Range filter values
            headcountRangeFilter={headcountRangeFilter}
            actualKcphRangeFilter={actualKcphRangeFilter}
            forecastedHoursRangeFilter={forecastedHoursRangeFilter}
            forecastedCasesRangeFilter={forecastedCasesRangeFilter}
            actualCasesRangeFilter={actualCasesRangeFilter}
            // Filter change handlers
            onPeriodFilterChange={handlePeriodFilterChange}
            onWeekFilterChange={handleWeekFilterChange}
            onYearFilterChange={handleYearFilterChange}
            onForecastedHoursFilterChange={handleForecastedHoursFilterChange}
            onScheduledHoursFilterChange={handleScheduledHoursFilterChange}
            onActualHoursFilterChange={handleActualHoursFilterChange}
            onActualCasesFilterChange={handleActualCasesFilterChange}
            onCreatedByFilterChange={handleCreatedByFilterChange}
            onModifiedByFilterChange={handleModifiedByFilterChange}
            // Range filter change handlers
            onHeadcountRangeFilterChange={handleHeadcountRangeFilterChange}
            onActualKcphRangeFilterChange={handleActualKcphRangeFilterChange}
            onForecastedHoursRangeFilterChange={handleForecastedHoursRangeFilterChange}
            onForecastedCasesRangeFilterChange={handleForecastedCasesRangeFilterChange}
            onActualCasesRangeFilterChange={handleActualCasesRangeFilterChange}
            // Legacy handlers (keeping for backward compatibility)
            onPeriodFilterPress={() => console.log('Period filter')}
            onWeekFilterPress={() => console.log('Week filter')}
            onYearFilterPress={() => console.log('Year filter')}
            onForecastedCasesFilterPress={() => console.log('Forecasted Cases filter')}
            onActualCasesFilterPress={() => console.log('Actual Cases filter')}
            onCreatedByFilterPress={() => console.log('Created by filter')}
            onModifiedByFilterPress={() => console.log('Modified by filter')}
            onAllFiltersPress={() => console.log('All Filters')}
            onResetAllPress={handleResetAllFilters}
            onEditPress={handleEdit}
            onCopySchedulePress={handleCopySchedule}
            onCopyScheduleFromTable={handleCopyScheduleFromTable}
            onDeletePress={handleDelete}
          />
          <DLOScheduleTableContent
            data={getPaginatedData()}
            loading={scheduleLoading}
            onRowSelect={handleRowSelect}
            onRowFavorite={handleRowFavorite}
            onHeaderSort={handleHeaderSort}
          />
          <DLOScheduleTablePagination
            currentPage={currentPage}
            totalPages={getTotalPages()}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            totalItems={getTotalItems()}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </View>
      </View>

      {/* Create Schedule Modal */}
      <DLOCreateScheduleModal
        visible={showCreateScheduleModal}
        onClose={handleCloseModal}
        onCopySchedule={handleCopyScheduleFromModal}
        currentPeriod={currentValues.period}
        currentWeek={currentValues.week}
        currentYear={currentValues.year}
        scheduleTableData={getAllTableData()}
      />

      {/* Filter Modal */}
      <DLOModal
        visible={showFilterModal}
        onClose={handleCloseFilterModal}
        title="Chart Filters"
        primaryButtonText="Apply Filters"
        secondaryButtonText="Cancel"
        onPrimaryPress={handleApplyFilters}
        onSecondaryPress={handleCloseFilterModal}
      >
        <View style={styles.filterModalContent}>
          <View style={styles.filterRow1}>
            <View style={styles.filterDropdownWrapper}>
              <DLODropdown
                label="Period"
                value={tempFilters.period}
                disableSorting={true}
                options={getDLOPeriodOptions()}
                onSelect={(option) => handleTempFilterChange('period', option)}
                placeholder="Select period"
              />
            </View>
            
            <View style={styles.filterDropdownWrapper}>
              <DLODropdown
                label="Week"
                value={tempFilters.week}
                disableSorting={true}
                options={getDLOWeekOptions()}
                onSelect={(option) => handleTempFilterChange('week', option)}
                placeholder="Select week"
              />
            </View>
          </View>
          
          <View style={styles.filterRow2}>
            <View style={styles.filterDropdownWrapper}>
              <DLODropdown
                label="Year"
                value={tempFilters.year}
                options={getDLOYearOptions()}
                onSelect={(option) => handleTempFilterChange('year', option)}
                placeholder="Select year"
              />
            </View>
          </View>
        </View>
      </DLOModal>
    </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  fullScreenLoaderOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(251, 252, 254, 0.85)',
    zIndex: 9999,
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  fullScreenLoaderText: {
    marginTop: 16,
    fontSize: 16,
    color: '#0066CC',
    fontWeight: '600',
    textAlign: 'center',
  },
  container: {
    flex: 1,
  },
  headerContainer: {
    backgroundColor: 'rgb(240, 245, 252)',
    //paddingHorizontal: 20,
    paddingVertical: 16,
  },
  chartContainer: {
    backgroundColor: 'rgb(240, 245, 252)',
    //paddingHorizontal: 20,
    paddingVertical: 16,
    marginBottom: 10,
  },
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 16,
  },
  chartsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 20,
    height: 360,
  },
  chartLeft: {
    flex: 1,
    height: '100%',
  },
  chartRight: {
    flex: 1,
    height: '100%',
  },
  content: {
    flex: 1,
    backgroundColor: 'rgb(240, 245, 252)',
    padding: 20,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    //marginHorizontal: 20,
    marginBottom: 20,
  },
  contentText: {
    fontSize: 18,
    color: '#34495e',
    marginBottom: 20,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  componentSection: {
    marginBottom: 30,
    padding: 15,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  componentTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#495057',
    marginBottom: 15,
    textAlign: 'center',
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 20,
  },
  statCard: {
    backgroundColor: '#ecf0f1',
    padding: 20,
    borderRadius: 8,
    alignItems: 'center',
    minWidth: 100,
  },
  statValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  statLabel: {
    fontSize: 14,
    color: '#7f8c8d',
    marginTop: 5,
  },
  scheduleTableContainer: {
    backgroundColor: 'rgb(240, 245, 252)',
    paddingVertical: 16,
    marginBottom: 10,
  },
  scheduleTableTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 10,
    textAlign: 'center',
    color: '#34495e',
  },
  scheduleTableWrapper: {
    backgroundColor: '#FBFCFE',
    borderRadius: 8,
    overflow: 'hidden',
    padding: '1%'
  },
  chartLoadingContainer: {
    position: 'relative',
    flex: 1,
  },
  chartLoadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(251, 252, 254, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
  },
  chartErrorContainer: {
    position: 'relative',
    flex: 1,
  },
  chartErrorOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(251, 252, 254, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#0066CC',
    fontWeight: '600',
    textAlign: 'center',
  },
  errorText: {
    fontSize: 14,
    color: '#D40020',
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 4,
  },
  errorSubText: {
    fontSize: 12,
    color: '#616161',
    textAlign: 'center',
  },
  // New filter styles
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 12,
  },
  filterDisplayText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#3A3A3A',
    backgroundColor: '#E8F2FF',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#B3D9FF',
  },
  filtersButton: {
    backgroundColor: '#0066CC',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  filtersButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  filterModalContent: {
    gap: 16,
  },
  filterRow1: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'flex-end',
  },
  filterRow2: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
  },
  filterDropdownWrapper: {
    flex: 1,
    minWidth: 0, // Allow dropdown to shrink
  },
});

// Loader overlay styles
const fullScreenLoaderStyles = {
  fullScreenLoaderOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(251, 252, 254, 0.85)',
    zIndex: 9999,
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  fullScreenLoaderText: {
    marginTop: 16,
    fontSize: 16,
    color: '#0066CC',
    fontWeight: '600',
    textAlign: 'center',
  },
};

// Merge loader styles into main styles
Object.assign(styles, fullScreenLoaderStyles);
